/**
 * Development logging that does not follow the app into production.
 *
 * There were 175 bare `console.log` calls in this frontend, and they printed
 * what they were debugging: "Dashboard loaded health data" with the records,
 * "Reloaded medication data" with the medications, a provider registration form
 * with the password still in it. A browser console is not a private place -
 * it is readable by anything running on the page, by anyone the phone is handed
 * to, and by any extension the user has installed. Health records and
 * credentials do not belong there.
 *
 * `log.debug` and `log.info` are silent in a production build. Vite replaces
 * `import.meta.env.DEV` with a literal, so the calls fold away rather than
 * running and discarding their arguments - which also means an expensive
 * argument is still evaluated, so do not put work inside the call.
 *
 * `warn` and `error` pass straight through in every build: a real failure
 * should be visible in a bug report. Keep payloads out of them all the same -
 * log what failed, not the record it failed on.
 */

const isDev = import.meta.env.DEV;

/* eslint-disable no-console -- this module is the one place console belongs;
   the rule exists to send every other caller here. */

export const log = {
  debug: (...args: unknown[]): void => {
    if (isDev) console.debug(...args);
  },
  info: (...args: unknown[]): void => {
    if (isDev) console.info(...args);
  },
  warn: (...args: unknown[]): void => {
    console.warn(...args);
  },
  error: (...args: unknown[]): void => {
    console.error(...args);
  },
};

export default log;
